import { getTeamOverall, EMPTY_COMP_STATS, getPositionCompatibility, getSlotPositionFor } from './gameData';
import { generateCommentary } from './commentary';

// Match event types
const EVENT_TYPES = {
  GOAL: 'goal', ASSIST: 'assist', YELLOW_CARD: 'yellow_card', RED_CARD: 'red_card',
  SUBSTITUTION: 'substitution', CHANCE: 'chance', SAVE: 'save', FOUL: 'foul',
  HALFTIME: 'halftime', FULLTIME: 'fulltime', KICKOFF: 'kickoff', INJURY: 'injury'
};

const randomMinute = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const getRandomPlayer = (team, excludeGK = false) => {
  const lineup = team.lineup
    .map(num => team.players.find(p => p.number === num))
    .filter(p => p && (!excludeGK || p.position !== 'GK'));
  return lineup[Math.floor(Math.random() * lineup.length)];
};
const getAttackingPlayer = (team) => {
  const attack = ['ST','CF','LW','RW','CAM','SS','LF','RF'];
  const lineup = team.lineup.map(n => team.players.find(p => p.number === n)).filter(p => p && attack.includes(p.position));
  if (!lineup.length) return getRandomPlayer(team, true);
  return lineup[Math.floor(Math.random() * lineup.length)];
};
const getMidfielder = (team) => {
  const mid = ['CM','CAM','CDM','LM','RM','LW','RW'];
  const lineup = team.lineup.map(n => team.players.find(p => p.number === n)).filter(p => p && mid.includes(p.position));
  if (!lineup.length) return getRandomPlayer(team, true);
  return lineup[Math.floor(Math.random() * lineup.length)];
};

const calculateGoalProbability = (attackingTeam, defendingTeam, isHome = false) => {
  const attackOvr = getTeamOverall(attackingTeam);
  const defendOvr = getTeamOverall(defendingTeam);
  const diff = attackOvr - defendOvr;
  // Higher base + bigger diff multiplier so rating gap matters more.
  // Tiny home stadium pulse (+1.5%) gives a small but real edge to the home side.
  let base = 0.30 + diff * 0.012;
  if (isHome) base += 0.015;
  return Math.max(0.12, Math.min(0.46, base));
};

const generatePeriodEvents = (homeTeam, awayTeam, startMinute, endMinute, homeScore, awayScore) => {
  const events = [];
  let currentMinute = startMinute;
  let newHomeScore = homeScore;
  let newAwayScore = awayScore;
  const homeSubs = { done: 0, max: 3 };
  const awaySubs = { done: 0, max: 3 };

  while (currentMinute < endMinute) {
    currentMinute += randomMinute(2, 7);
    if (currentMinute >= endMinute) break;
    const homeProb = calculateGoalProbability(homeTeam, awayTeam, true);
    const awayProb = calculateGoalProbability(awayTeam, homeTeam, false);
    const eventRoll = Math.random();
    if (eventRoll < homeProb / 2) {
      const scorer = getAttackingPlayer(homeTeam);
      const hasAssist = Math.random() > 0.3;
      const assister = hasAssist ? getMidfielder(homeTeam) : null;
      newHomeScore++;
      events.push({ type: EVENT_TYPES.GOAL, minute: currentMinute, team: 'home', teamName: homeTeam.name, player: scorer, assister });
    } else if (eventRoll < (homeProb + awayProb) / 2) {
      const scorer = getAttackingPlayer(awayTeam);
      const hasAssist = Math.random() > 0.3;
      const assister = hasAssist ? getMidfielder(awayTeam) : null;
      newAwayScore++;
      events.push({ type: EVENT_TYPES.GOAL, minute: currentMinute, team: 'away', teamName: awayTeam.name, player: scorer, assister });
    } else if (eventRoll < 0.2) {
      const isHome = Math.random() > 0.5;
      const t = isHome ? homeTeam : awayTeam;
      events.push({ type: EVENT_TYPES.SAVE, minute: currentMinute, team: isHome ? 'home' : 'away', teamName: t.name, player: getAttackingPlayer(t) });
    } else if (eventRoll < 0.25) {
      const isHome = Math.random() > 0.5;
      const t = isHome ? homeTeam : awayTeam;
      events.push({ type: EVENT_TYPES.YELLOW_CARD, minute: currentMinute, team: isHome ? 'home' : 'away', teamName: t.name, player: getRandomPlayer(t, true) });
    } else if (eventRoll < 0.26) {
      const isHome = Math.random() > 0.5;
      const t = isHome ? homeTeam : awayTeam;
      events.push({ type: EVENT_TYPES.RED_CARD, minute: currentMinute, team: isHome ? 'home' : 'away', teamName: t.name, player: getRandomPlayer(t, true) });
    } else if (eventRoll < 0.268) {
      const isHome = Math.random() > 0.5;
      const t = isHome ? homeTeam : awayTeam;
      events.push({ type: EVENT_TYPES.INJURY, minute: currentMinute, team: isHome ? 'home' : 'away', teamName: t.name, player: getRandomPlayer(t, true), injuryWeeks: Math.floor(Math.random() * 6) + 1 });
    }

    // Auto-substitutions (common, happen in second half between 55-85 min)
    if (currentMinute >= 55 && currentMinute <= 85) {
      const tryAutoSub = (team, side, subTracker) => {
        if (subTracker.done >= subTracker.max) return;
        if (Math.random() > 0.12) return; // ~12% chance per check
        const benchNums = team.bench.filter(n => {
          const bp = team.players.find(p => p.number === n);
          return bp && (!bp.injuredWeeks || bp.injuredWeeks <= 0);
        });
        if (benchNums.length === 0) return;
        const playerOut = getRandomPlayer(team, true);
        if (!playerOut) return;
        const subInNum = benchNums[Math.floor(Math.random() * benchNums.length)];
        const playerIn = team.players.find(p => p.number === subInNum);
        if (!playerIn || playerIn.id === playerOut.id) return;
        subTracker.done++;
        events.push({
          type: EVENT_TYPES.SUBSTITUTION, minute: currentMinute,
          team: side, teamName: team.name,
          playerOut, playerIn,
          commentary: `Tactical change! ${playerOut.name} makes way for ${playerIn.name}.`
        });
      };
      tryAutoSub(homeTeam, 'home', homeSubs);
      tryAutoSub(awayTeam, 'away', awaySubs);
    }
  }
  return { events, homeScore: newHomeScore, awayScore: newAwayScore };
};

export const generateMatchRatings = (homeTeam, awayTeam, events) => {
  const ratings = {};
  // Build {player, team} pairs so we can evaluate out-of-position penalty per player
  const all = [
    ...homeTeam.lineup.map(n => { const p = homeTeam.players.find(pp => pp.number === n); return p ? { player: p, team: homeTeam, side: 'home' } : null; }).filter(Boolean),
    ...awayTeam.lineup.map(n => { const p = awayTeam.players.find(pp => pp.number === n); return p ? { player: p, team: awayTeam, side: 'away' } : null; }).filter(Boolean)
  ];
  // Goals conceded — used for clean-sheet bonus on GK / defenders
  const homeGoals = events.filter(e => e.type === 'goal' && e.team === 'home').length;
  const awayGoals = events.filter(e => e.type === 'goal' && e.team === 'away').length;
  // Result for team-level swing (winning team gets a small lift, losing team a small dip)
  const homeResult = homeGoals === awayGoals ? 0 : (homeGoals > awayGoals ? 1 : -1);
  all.forEach(({ player: p, team, side }) => {
    // Baseline: ~6.0-6.6 average so a routine performance lands a hair above 6.0
    // and event bonuses push standout players past the thresholds the user
    // asked for: > 7.0 normal-good, > 8.0 good, > 9.0 great, 10.0 perfect.
    let base = 5.6 + Math.random() * 1.2; // 5.6 – 6.8
    // Overall-based seed: a 85-ovr player gets +0.35, a 65-ovr gets -0.35
    base += (((p.overall ?? 75) - 75) * 0.035);
    // Team result swing — winners get +0.25 average, losers -0.25, draw 0
    const teamResult = side === 'home' ? homeResult : -homeResult;
    base += teamResult * 0.25;
    // Out-of-position penalty (compat 1.0 → 0, 0.35 → -2.2)
    const slot = getSlotPositionFor(team, p.number);
    const compat = getPositionCompatibility(p.position, slot);
    base += (compat - 1) * 3.4;
    // Event bonuses — chunky so a goal + assist clearly crosses 8.0,
    // hat-trick + assist can reach 10.0.
    let goals = 0;
    events.forEach(e => {
      if (e.player?.id === p.id) {
        if (e.type === 'goal')        { base += 1.30 + Math.random() * 0.45; goals++; }
        else if (e.type === 'save')   { base += 0.45; }
        else if (e.type === 'yellow_card') { base -= 0.55; }
        else if (e.type === 'red_card')    { base -= 2.30; }
        else if (e.type === 'injury')      { base -= 0.40; }
      }
      if (e.assister?.id === p.id) base += 0.85;
    });
    // Hat-trick bonus on top of per-goal — pushes elite performances toward 10.0
    if (goals >= 3) base += 0.65;
    // Clean-sheet bonus for GK + defenders when their side conceded zero
    const goalsConceded = side === 'home' ? awayGoals : homeGoals;
    if (goalsConceded === 0 && (p.position === 'GK' || ['CB', 'LB', 'RB', 'LWB', 'RWB'].includes(p.position))) {
      base += p.position === 'GK' ? 0.55 : 0.30;
    }
    ratings[p.id] = Math.round(Math.max(4.3, Math.min(10.0, base)) * 10) / 10;
  });
  return ratings;
};

// Quick simulation
export const quickSimulate = (homeTeam, awayTeam) => {
  const allEvents = [];
  allEvents.push({ type: EVENT_TYPES.KICKOFF, minute: 0, message: 'Match kicks off!', commentary: generateCommentary({ type: 'kickoff' }, homeTeam, awayTeam) });
  const fh = generatePeriodEvents(homeTeam, awayTeam, 0, 45, 0, 0);
  fh.events.forEach(e => { e.commentary = generateCommentary(e, homeTeam, awayTeam); });
  allEvents.push(...fh.events);
  const ht = { type: EVENT_TYPES.HALFTIME, minute: 45, homeScore: fh.homeScore, awayScore: fh.awayScore, message: `Half Time: ${homeTeam.name} ${fh.homeScore} - ${fh.awayScore} ${awayTeam.name}` };
  ht.commentary = generateCommentary(ht, homeTeam, awayTeam);
  allEvents.push(ht);
  const sh = generatePeriodEvents(homeTeam, awayTeam, 45, 90, fh.homeScore, fh.awayScore);
  sh.events.forEach(e => { e.commentary = generateCommentary(e, homeTeam, awayTeam); });
  allEvents.push(...sh.events);
  const ft = { type: EVENT_TYPES.FULLTIME, minute: 90, homeScore: sh.homeScore, awayScore: sh.awayScore, message: `Full Time: ${homeTeam.name} ${sh.homeScore} - ${sh.awayScore} ${awayTeam.name}` };
  ft.commentary = generateCommentary(ft, homeTeam, awayTeam);
  allEvents.push(ft);
  const injuries = allEvents.filter(e => e.type === EVENT_TYPES.INJURY);
  const matchRatings = generateMatchRatings(homeTeam, awayTeam, allEvents);
  return { homeScore: sh.homeScore, awayScore: sh.awayScore, events: allEvents, injuries, matchRatings, played: true };
};

/**
 * Simulate extra time period (30 minutes) between two teams.
 * Returns accumulated additional goals and events.
 */
export const simulateExtraTime = (homeTeam, awayTeam, startHomeScore = 0, startAwayScore = 0) => {
  const events = [];
  events.push({ type: 'extra_time_start', minute: 90, message: 'Extra Time begins!', commentary: `Extra Time! ${homeTeam.name} ${startHomeScore} - ${startAwayScore} ${awayTeam.name}` });
  const et1 = generatePeriodEvents(homeTeam, awayTeam, 90, 105, startHomeScore, startAwayScore);
  et1.events.forEach(e => { e.commentary = generateCommentary(e, homeTeam, awayTeam); });
  events.push(...et1.events);
  events.push({ type: 'et_halftime', minute: 105, message: 'Extra Time Half', commentary: `ET Half: ${et1.homeScore} - ${et1.awayScore}` });
  const et2 = generatePeriodEvents(homeTeam, awayTeam, 105, 120, et1.homeScore, et1.awayScore);
  et2.events.forEach(e => { e.commentary = generateCommentary(e, homeTeam, awayTeam); });
  events.push(...et2.events);
  events.push({ type: 'et_fulltime', minute: 120, homeScore: et2.homeScore, awayScore: et2.awayScore, message: `End of ET: ${homeTeam.name} ${et2.homeScore} - ${et2.awayScore} ${awayTeam.name}` });
  return { homeScore: et2.homeScore, awayScore: et2.awayScore, events };
};

/**
 * Simulate a penalty shootout between two teams. Classic 5-then-sudden-death format.
 */
export const simulatePenaltyShootout = (homeTeam, awayTeam) => {
  const events = [];
  const homeShooters = homeTeam.lineup.map(n => homeTeam.players.find(p => p.number === n)).filter(Boolean);
  const awayShooters = awayTeam.lineup.map(n => awayTeam.players.find(p => p.number === n)).filter(Boolean);
  // Sort by overall for shooting order
  homeShooters.sort((a, b) => b.overall - a.overall);
  awayShooters.sort((a, b) => b.overall - a.overall);
  let homePens = 0, awayPens = 0;
  const penResults = [];
  events.push({ type: 'pens_start', minute: 120, message: 'PENALTY SHOOTOUT!', commentary: 'The tie will be decided on penalties!' });
  const takePen = (player, team, side) => {
    const baseProb = 0.72 + (player.overall - 75) * 0.01;
    const scored = Math.random() < Math.max(0.5, Math.min(0.9, baseProb));
    penResults.push({ side, player, scored, teamName: team.name });
    if (scored) {
      if (side === 'home') homePens++; else awayPens++;
    }
    events.push({ type: 'penalty', minute: 120, side, player, scored, teamName: team.name,
      commentary: `${player.name} ${scored ? 'SCORES' : 'MISSES'} the penalty! ${homePens}-${awayPens}` });
  };
  // Best of 5
  for (let i = 0; i < 5; i++) {
    takePen(homeShooters[i % homeShooters.length], homeTeam, 'home');
    // If home already clinched, check early
    const homeLeft = 5 - (i + 1);
    const awayLeft = 5 - i;
    if (homePens > awayPens + awayLeft) break;
    takePen(awayShooters[i % awayShooters.length], awayTeam, 'away');
    const homeLeft2 = 5 - (i + 1);
    const awayLeft2 = 5 - (i + 1);
    if (awayPens > homePens + homeLeft2) break;
    if (homePens + homeLeft2 < awayPens) break;
  }
  // Sudden death
  let attempt = 5;
  while (homePens === awayPens) {
    takePen(homeShooters[attempt % homeShooters.length], homeTeam, 'home');
    takePen(awayShooters[attempt % awayShooters.length], awayTeam, 'away');
    attempt++;
    if (attempt > 15) break; // safety
  }
  const winner = homePens > awayPens ? 'home' : 'away';
  events.push({ type: 'pens_end', minute: 120, homeScore: homePens, awayScore: awayPens, winner,
    message: `Penalty Shootout: ${homeTeam.name} ${homePens} - ${awayPens} ${awayTeam.name}`,
    commentary: `${winner === 'home' ? homeTeam.name : awayTeam.name} win on penalties!` });
  return { homePens, awayPens, winner, events, penResults };
};

/**
 * Knockout-aware simulation - runs regulation, then extra time, then pens if needed.
 * Returns final winner (home | away) and full event log.
 */
export const simulateKnockoutMatch = (homeTeam, awayTeam, aggregateHomeExtra = 0, aggregateAwayExtra = 0) => {
  const base = quickSimulate(homeTeam, awayTeam);
  let homeAgg = base.homeScore + aggregateHomeExtra;
  let awayAgg = base.awayScore + aggregateAwayExtra;
  let result = { ...base };
  if (homeAgg === awayAgg) {
    const et = simulateExtraTime(homeTeam, awayTeam, base.homeScore, base.awayScore);
    result.events = [...result.events, ...et.events];
    result.etHomeScore = et.homeScore;
    result.etAwayScore = et.awayScore;
    homeAgg = et.homeScore + aggregateHomeExtra;
    awayAgg = et.awayScore + aggregateAwayExtra;
    // For same-leg aggregation: use et scores only
    result.homeScore = et.homeScore;
    result.awayScore = et.awayScore;
    if (homeAgg === awayAgg) {
      const pens = simulatePenaltyShootout(homeTeam, awayTeam);
      result.events = [...result.events, ...pens.events];
      result.pens = { home: pens.homePens, away: pens.awayPens, winner: pens.winner };
      result.winner = pens.winner;
    } else {
      result.winner = homeAgg > awayAgg ? 'home' : 'away';
    }
  } else {
    result.winner = homeAgg > awayAgg ? 'home' : 'away';
  }
  result.homeAggregate = homeAgg;
  result.awayAggregate = awayAgg;
  return result;
};

// Full simulation streams - with pause support and re-entry protection
export const fullSimulate = (homeTeam, awayTeam, onEvent, onComplete, speed = 100) => {
  const result = quickSimulate(homeTeam, awayTeam);
  const events = result.events;
  let currentIndex = 0;
  let currentMinute = 0;
  let paused = false;
  let stopped = false;
  let intervalId = null;

  const tick = () => {
    if (paused || stopped) return;
    if (currentIndex >= events.length) {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
      if (!stopped) onComplete(result);
      return;
    }
    const event = events[currentIndex];
    if (event.minute > currentMinute) {
      currentMinute++;
      try { onEvent({ type: 'minute', minute: currentMinute }); } catch {}
    } else {
      try { onEvent(event); } catch {}
      currentIndex++;
    }
  };

  intervalId = setInterval(tick, speed);

  return {
    stop: () => { stopped = true; if (intervalId) { clearInterval(intervalId); intervalId = null; } },
    pause: () => { paused = true; },
    resume: () => { paused = false; },
    isPaused: () => paused
  };
};

// Team season stats (league)
export const updateTeamStats = (team, goalsFor, goalsAgainst, isWin, isDraw) => ({
  ...team,
  stats: {
    ...team.stats,
    played: team.stats.played + 1,
    won: team.stats.won + (isWin ? 1 : 0),
    drawn: team.stats.drawn + (isDraw ? 1 : 0),
    lost: team.stats.lost + (!isWin && !isDraw ? 1 : 0),
    goalsFor: team.stats.goalsFor + goalsFor,
    goalsAgainst: team.stats.goalsAgainst + goalsAgainst,
    points: team.stats.points + (isWin ? 3 : isDraw ? 1 : 0)
  }
});

// Helper - ensure player has stats bucket for a given competition
const ensureStatsBucket = (player, comp) => {
  if (!player.stats) player.stats = { league: EMPTY_COMP_STATS(), cup: EMPTY_COMP_STATS(), championsLeague: EMPTY_COMP_STATS(), cwc: EMPTY_COMP_STATS() };
  if (!player.stats[comp]) player.stats[comp] = EMPTY_COMP_STATS();
};

/**
 * Update player stats based on match events for a specific competition.
 * @param {Array} teams - all teams (league or combined pool)
 * @param {Array} events
 * @param {String} homeTeamName
 * @param {String} awayTeamName
 * @param {Number} homeScore
 * @param {Number} awayScore
 * @param {String} competition - 'league' | 'cup' | 'championsLeague' | 'cwc'
 */
export const updatePlayerStats = (teams, events, homeTeamName, awayTeamName, homeScore, awayScore, competition = 'league', matchRatings = null) => {
  const updatedTeams = teams.map(t => ({
    ...t,
    players: t.players.map(p => ({ ...p, stats: { ...p.stats, [competition]: { ...(p.stats?.[competition] || EMPTY_COMP_STATS()) } } }))
  }));

  // matchesPlayed: +1 for every player on lineup of home/away team
  const addMatchPlayed = (teamName) => {
    const tIdx = updatedTeams.findIndex(t => t.name === teamName);
    if (tIdx === -1) return;
    const team = updatedTeams[tIdx];
    team.lineup.forEach(num => {
      const pIdx = team.players.findIndex(p => p.number === num);
      if (pIdx !== -1) {
        ensureStatsBucket(team.players[pIdx], competition);
        team.players[pIdx].stats[competition].matchesPlayed = (team.players[pIdx].stats[competition].matchesPlayed || 0) + 1;
      }
    });
  };
  if (homeTeamName) addMatchPlayed(homeTeamName);
  if (awayTeamName) addMatchPlayed(awayTeamName);

  events.forEach(event => {
    if ((event.type === 'goal' || event.type === 'yellow_card' || event.type === 'red_card') && event.player) {
      const tIdx = updatedTeams.findIndex(t => t.name === event.teamName);
      if (tIdx === -1) return;
      const pIdx = updatedTeams[tIdx].players.findIndex(p => p.id === event.player.id);
      if (pIdx === -1) return;
      ensureStatsBucket(updatedTeams[tIdx].players[pIdx], competition);
      const bucket = updatedTeams[tIdx].players[pIdx].stats[competition];
      if (event.type === 'goal') bucket.goals = (bucket.goals || 0) + 1;
      if (event.type === 'yellow_card') bucket.yellowCards = (bucket.yellowCards || 0) + 1;
      if (event.type === 'red_card') bucket.redCards = (bucket.redCards || 0) + 1;
      if (event.type === 'goal' && event.assister) {
        const aIdx = updatedTeams[tIdx].players.findIndex(p => p.id === event.assister.id);
        if (aIdx !== -1) {
          ensureStatsBucket(updatedTeams[tIdx].players[aIdx], competition);
          updatedTeams[tIdx].players[aIdx].stats[competition].assists = (updatedTeams[tIdx].players[aIdx].stats[competition].assists || 0) + 1;
        }
      }
    }
  });

  // Clean sheets for GKs
  if (homeTeamName && awayTeamName) {
    const addCS = (teamName) => {
      const tIdx = updatedTeams.findIndex(t => t.name === teamName);
      if (tIdx === -1) return;
      const team = updatedTeams[tIdx];
      const gk = team.players.find(p => p.position === 'GK' && team.lineup.includes(p.number));
      if (!gk) return;
      const gkIdx = team.players.findIndex(p => p.id === gk.id);
      ensureStatsBucket(team.players[gkIdx], competition);
      team.players[gkIdx].stats[competition].cleanSheets = (team.players[gkIdx].stats[competition].cleanSheets || 0) + 1;
    };
    if (awayScore === 0) addCS(homeTeamName);
    if (homeScore === 0) addCS(awayTeamName);
  }

  // Apply growth if matchRatings provided
  if (matchRatings) {
    for (let ti = 0; ti < updatedTeams.length; ti++) {
      const team = updatedTeams[ti];
      for (let pi = 0; pi < team.players.length; pi++) {
        const p = team.players[pi];
        const rating = matchRatings[p.id];
        if (rating == null) continue;
        const delta = (rating - 6.5) * 0.35;
        const points = (p.growthPoints || 0) + delta;
        let overall = p.overall;
        let gp = points;
        const cap = p.potential || 99;
        while (gp >= 1 && overall < cap) { overall += 1; gp -= 1; }
        while (gp <= -2 && overall > 40) { overall -= 1; gp += 2; }
        team.players[pi] = { ...p, overall, growthPoints: gp };
      }
    }
  }

  return updatedTeams;
};

/**
 * Update player overalls based on match ratings.
 * Gradual growth: ratings above 7.0 add progress, below 6.0 subtract.
 * Progress accumulates via `growthPoints`; +1 overall per +1.0 points (capped at potential).
 */
export const applyMatchGrowth = (teams, matchRatings) => {
  if (!matchRatings) return teams;
  return teams.map(team => ({
    ...team,
    players: team.players.map(p => {
      const rating = matchRatings[p.id];
      if (rating == null) return p;
      const delta = (rating - 6.5) * 0.35; // 8.0 → +0.52, 9.0 → +0.87, 5.0 → -0.52
      const points = (p.growthPoints || 0) + delta;
      let overall = p.overall;
      let gp = points;
      const cap = p.potential || 99;
      while (gp >= 1 && overall < cap) { overall += 1; gp -= 1; }
      while (gp <= -2 && overall > 40) { overall -= 1; gp += 2; }
      return { ...p, overall, growthPoints: gp };
    })
  }));
};

/**
 * Record a trophy win on every player in a team.
 * trophyHistory on player is an array: [{season, year, competition, competitionLabel}]
 */
export const recordTeamTrophy = (teams, winnerId, trophyEntry) => {
  return teams.map(t => {
    if (t.id !== winnerId) return t;
    return {
      ...t,
      players: t.players.map(p => ({
        ...p,
        trophies: [...(p.trophies || []), trophyEntry]
      }))
    };
  });
};

export const makeSubstitution = (team, playerOutNumber, playerInNumber) => ({
  ...team,
  lineup: team.lineup.map(num => num === playerOutNumber ? playerInNumber : num),
  bench: team.bench.map(num => num === playerInNumber ? playerOutNumber : num)
});
export const changeFormation = (team, newFormation) => ({ ...team, formation: newFormation });

// Simulate entire league week
export const simulateWeek = (teams, fixtures, weekNumber) => {
  const weekFixtures = fixtures.filter(f => f.week === weekNumber && !f.played);
  let updatedTeams = [...teams];
  let updatedFixtures = [...fixtures];

  // Decrement injuries
  updatedTeams = updatedTeams.map(team => ({
    ...team,
    players: team.players.map(p => p.injuredWeeks && p.injuredWeeks > 0 ? { ...p, injuredWeeks: p.injuredWeeks - 1 } : p)
  }));
  // Auto-bench injured
  updatedTeams = updatedTeams.map(team => {
    let { lineup, bench, players } = team;
    const injuredInLineup = lineup.filter(num => {
      const p = players.find(pl => pl.number === num);
      return p && p.injuredWeeks && p.injuredWeeks > 0;
    });
    if (injuredInLineup.length > 0) {
      let newLineup = [...lineup];
      let newBench = [...bench];
      injuredInLineup.forEach(injNum => {
        const healthySub = newBench.find(bNum => {
          const bp = players.find(pl => pl.number === bNum);
          return bp && (!bp.injuredWeeks || bp.injuredWeeks <= 0);
        });
        if (healthySub) {
          newLineup = newLineup.map(n => n === injNum ? healthySub : n);
          newBench = newBench.filter(n => n !== healthySub);
          newBench.push(injNum);
        }
      });
      return { ...team, lineup: newLineup, bench: newBench };
    }
    return team;
  });

  weekFixtures.forEach(fixture => {
    const homeTeam = updatedTeams.find(t => t.id === fixture.homeTeamId);
    const awayTeam = updatedTeams.find(t => t.id === fixture.awayTeamId);
    if (!homeTeam || !awayTeam) return;
    const result = quickSimulate(homeTeam, awayTeam);
    const fixtureIndex = updatedFixtures.findIndex(f => f.id === fixture.id);
    updatedFixtures[fixtureIndex] = {
      ...fixture,
      homeScore: result.homeScore,
      awayScore: result.awayScore,
      played: true,
      events: result.events,
      matchRatings: result.matchRatings
    };
    const isHomeWin = result.homeScore > result.awayScore;
    const isDraw = result.homeScore === result.awayScore;
    const homeIndex = updatedTeams.findIndex(t => t.id === homeTeam.id);
    const awayIndex = updatedTeams.findIndex(t => t.id === awayTeam.id);
    updatedTeams[homeIndex] = updateTeamStats(homeTeam, result.homeScore, result.awayScore, isHomeWin, isDraw);
    updatedTeams[awayIndex] = updateTeamStats(awayTeam, result.awayScore, result.homeScore, !isHomeWin && !isDraw, isDraw);
    updatedTeams = updatePlayerStats(updatedTeams, result.events, homeTeam.name, awayTeam.name, result.homeScore, result.awayScore, 'league');
    updatedTeams = applyMatchGrowth(updatedTeams, result.matchRatings);
    if (result.injuries) {
      result.injuries.forEach(inj => {
        const tIdx = updatedTeams.findIndex(t => t.name === inj.teamName);
        if (tIdx !== -1 && inj.player) {
          const pIdx = updatedTeams[tIdx].players.findIndex(p => p.id === inj.player.id);
          if (pIdx !== -1) updatedTeams[tIdx].players[pIdx] = { ...updatedTeams[tIdx].players[pIdx], injuredWeeks: inj.injuryWeeks };
        }
      });
    }
  });
  return { teams: updatedTeams, fixtures: updatedFixtures };
};
